/**
 * Sessao salva no aparelho.
 *
 * `@flos/auth` e quem sabe falar com o Google e validar tokens. Enquanto esse
 * pacote nao estiver ligado — e mesmo depois, como reserva — a casca precisa de
 * conseguir dizer quem esta a usar o aplicativo sem rede nenhuma.
 *
 * Decisao que importa (ADR-0002): **um token expirado offline nao tranca
 * ninguem fora dos seus proprios dados locais**. Os dados estao no aparelho, sao
 * dela, e nao ha servidor nenhum para consultar. Degradamos para leitura-apenas
 * com um aviso visivel, em vez de mandar para a tela de entrada uma pessoa que
 * nao tem como entrar.
 */
import { DEFAULT_ROLE_SCOPES, createPermissionSet, type FamilyRole } from '@flos/shared';
import type { ReadOnlyState, RestoredSession } from './ports';

const SESSION_KEY = 'flos.session';

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari em navegacao privada lança ao tocar em localStorage.
    return null;
  }
}

function isSession(value: unknown): value is RestoredSession {
  if (typeof value !== 'object' || value === null) return false;
  const info = (value as { info?: unknown }).info;
  if (typeof info !== 'object' || info === null) return false;
  const s = info as Record<string, unknown>;
  return (
    typeof s['memberId'] === 'string' &&
    typeof s['familyId'] === 'string' &&
    typeof s['displayName'] === 'string' &&
    typeof s['role'] === 'string'
  );
}

export function readStoredSession(): RestoredSession | null {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isSession(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeStoredSession(session: RestoredSession | null): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    if (session) storage.setItem(SESSION_KEY, JSON.stringify(session));
    else storage.removeItem(SESSION_KEY);
  } catch {
    // Sem persistencia continua a dar para usar o aplicativo nesta sessao.
  }
}

export function isExpired(session: RestoredSession, now: Date = new Date()): boolean {
  if (!session.expiresAt) return false;
  const at = new Date(session.expiresAt);
  if (Number.isNaN(at.getTime())) return false;
  return at.getTime() <= now.getTime();
}

/** Permissoes efetivas de uma sessao, com o papel ja expandido. */
export function permissionsFor(session: RestoredSession) {
  if (session.permissions) return session.permissions;
  const role = session.info.role as FamilyRole;
  return createPermissionSet(role in DEFAULT_ROLE_SCOPES ? role : 'viewer');
}

/**
 * Permissoes reduzidas a leitura.
 *
 * Nao inventamos uma lista de escopos: negamos explicitamente escrita, remocao e
 * administracao em todos os plugins. A negacao ganha sempre sobre o papel, o que
 * torna isto seguro mesmo para um `owner`.
 */
export function toReadOnlyPermissions(session: RestoredSession) {
  const base = permissionsFor(session);
  return {
    ...base,
    denied: [...new Set([...base.denied, '*:write', '*:delete', '*:admin'])],
  };
}

export const EXPIRED_OFFLINE: ReadOnlyState = {
  reason: 'sessao-expirada-offline',
  title: 'A sua ligação à conta expirou',
  message:
    'Sem internet não consigo confirmar a sua conta, por isso os seus dados estão sendo mostrados só para leitura. Assim que houver ligação, tudo volta ao normal.',
};

export interface SessionDecision {
  session: RestoredSession | null;
  readOnly: ReadOnlyState | null;
  /** `true` quando e preciso passar pela tela de entrada. */
  needsSignIn: boolean;
}

/**
 * Decide o que fazer com a sessao salva. Sem I/O: e uma funcao pura, para
 * poder ser testada sem browser nem rede.
 */
export function decideSession(input: {
  stored: RestoredSession | null;
  online: boolean;
  now?: Date;
}): SessionDecision {
  const { stored, online } = input;
  if (!stored) return { session: null, readOnly: null, needsSignIn: true };

  if (!isExpired(stored, input.now ?? new Date())) {
    return { session: stored, readOnly: null, needsSignIn: false };
  }

  if (!online) {
    // O caso que interessa: expirou, nao ha rede, os dados sao dela e estao aqui.
    return {
      session: { ...stored, permissions: toReadOnlyPermissions(stored) },
      readOnly: EXPIRED_OFFLINE,
      needsSignIn: false,
    };
  }

  // Expirou e ha rede: da para renovar a serio, portanto pedimos entrada.
  return { session: null, readOnly: null, needsSignIn: true };
}
